// MaturaSequenceNthTermClosedTask.js
// Zadanie maturalne zamknięte – ciąg ogólny:
// a_n = (A * (-1)^{n lub n+1}) / b^{n-1}, policz k-ty wyraz (ABCD).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

export const formatMultipliedFactorLatex = (value) => (
  Number(value) < 0 ? `(${value})` : String(value)
);

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
// ======================= GENERATOR =======================

function createOriginalTask() {
  const options = [-2, -1, 1, 2].map((value, index) => ({
    value,
    latex: String(value),
    label: LETTERS[index],
    isCorrect: value === 1,
  }));

  return {
    type: "MaturaSequenceNthTermClosedTask",
    data: {
      base: 2,
      A: 32,
      c: 1,
      eps: 0,
      askedIndex: 6,
      formulaLatex: "a_n = \\dfrac{32\\cdot(-1)^n}{2^{n-1}}",
      options,
      correctIndex: 2,
      correctValue: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 15,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // baza w mianowniku – tylko 2, 3, 5
  const possibleBases = [2, 3, 5];
  const base = pick(possibleBases);

  // ograniczenia na maksymalną potęgę base^{n-1} w A = c * base^{n-1}
  // 2 -> max 2^7, 3 -> max 3^4, 5 -> max 5^3
  const maxExpByBase = {
    2: 7,
    3: 4,
    5: 3,
  };

  const maxExp = maxExpByBase[base];

  // numer wyrazu, o który pytamy – jak wcześniej 4–8,
  // ale przycięty tak, żeby n-1 <= maxExp
  const minN = 4;
  const maxN = Math.min(8, maxExp + 1); // bo n-1 <= maxExp
  const askedIndex = randInt(minN, maxN);

  // mały mnożnik przed ułamkiem (żeby wynik był ±1 lub ±2)
  const c = pick([1, 2]);

  // czy mamy (-1)^n czy (-1)^{n+1}
  const eps = pick([0, 1]); // 0 → n, 1 → n+1

  // ustawiamy A tak, żeby a_k wyszło ładną liczbą całkowitą
  // A = c * base^{askedIndex-1}, ale z powyższym ograniczeniem na askedIndex
  const A = c * Math.pow(base, askedIndex - 1);

  const signExponentLatex = eps === 0 ? "n" : "n+1";

  const formulaLatex = `a_n = \\dfrac{${A}\\cdot(-1)^{${signExponentLatex}}}{${base}^{n-1}}`;

  // prawidłowy znak dla a_k
  const sign = (askedIndex + eps) % 2 === 0 ? 1 : -1;
  const correctValue = c * sign;

  // cztery odpowiedzi (ładne, małe liczby całkowite)
  const optionValues = [
    correctValue,
    -correctValue,
    correctValue + 1,
    correctValue - 1,
  ];

  // upewniamy się, że są różne
  const uniqueValues = Array.from(new Set(optionValues));
  while (uniqueValues.length < 4) {
    uniqueValues.push(uniqueValues[uniqueValues.length - 1] + 1);
  }

  const optionsShuffled = shuffle(
    uniqueValues.slice(0, 4).map((val) => ({
      value: val,
      latex: String(val),
    }))
  );

  const options = optionsShuffled.map((opt, idx) => ({
    ...opt,
    label: LETTERS[idx],
    isCorrect: opt.value === correctValue,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaSequenceNthTermClosedTask",
    data: {
      base,
      A,
      c,
      eps,
      askedIndex,
      formulaLatex,
      options,
      correctIndex,
      correctValue,
    },
  };
}

// ======================= RENDER TREŚCI =======================

export function renderTask({ data }) {
  const { formulaLatex, askedIndex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
        <p>
        Ciąg <InlineMath math={'(a_n)'} /> jest określony wzorem{" "}
        <InlineMath math={formulaLatex} /> dla każdej liczby naturalnej{" "}
        <InlineMath math={'n \\geq 1\\text{.}'} />
        </p>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Wyraz ciągu <InlineMath math={'(a_n)'} /> o numerze{" "}
        <InlineMath math={String(askedIndex)} /> jest równy:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================= INPUT (ABCD) =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= POPRAWNA ODPOWIEDŹ =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return (
    <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
  );
}

// ======================= WYJAŚNIENIE =======================

export function renderExplanation({ data }) {
  const { base, A, c, eps, askedIndex, correctValue, formulaLatex } = data;

  const signExponentLatex = eps === 0 ? "n" : "n+1";
  const generalLatex =
    formulaLatex ||
    `a_n = \\dfrac{${A}\\cdot(-1)^{${signExponentLatex}}}{${base}^{n-1}}`;

  const kLatex = String(askedIndex);
  const kMinus1 = askedIndex - 1;
  const kMinus1Latex = String(kMinus1);

  const basePower = Math.pow(base, kMinus1); // np. 5^3 = 125
  const exponentForMinusOne = eps === 0 ? askedIndex : askedIndex + 1;
  const exponentLatex = String(exponentForMinusOne);
  const minusOneValue = exponentForMinusOne % 2 === 0 ? 1 : -1;

  // Krok 2 – podstawianie
  const step2_1 = `a_{${kLatex}} = \\dfrac{${A}\\cdot(-1)^{${kLatex}${
    eps === 0 ? "" : "+1"
  }}}{${base}^{${kMinus1Latex}}}`;
  const step2_2 = `a_{${kLatex}} = \\dfrac{${c}\\cdot${base}^{${kMinus1Latex}}\\cdot(-1)^{${kLatex}${
    eps === 0 ? "" : "+1"
  }}}{${base}^{${kMinus1Latex}}}`;
  const step2_3 = `a_{${kLatex}} = ${c}\\cdot(-1)^{${kLatex}${
    eps === 0 ? "" : "+1"
  }}`;

  const step3_1 = `(-1)^{${exponentLatex}} = ${minusOneValue}`;
  const step3_2 = `a_{${kLatex}} = ${c}\\cdot${formatMultipliedFactorLatex(minusOneValue)} = ${correctValue}`;
  const finalValueLatex = `${correctValue}\\text{.}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Zapisujemy wyraz ogólny ciągu i
        rozkładamy licznik tak, żeby było widać potęgę podstawy:
      </p>
      <BlockMath math={generalLatex} />
      <BlockMath
        math={`A = ${A} = ${c}\\cdot${basePower} = ${c}\\cdot${base}^{${kMinus1Latex}}`}
      />
      <p>
        W naszym przykładzie{" "}
        <InlineMath math={`${base}^{${kMinus1Latex}} = ${basePower}`} /> (np.{" "}
        <InlineMath math={'5^{3} = 125'} />), a licznik to{" "}
        <InlineMath math={`${A} = ${c}\\cdot${basePower}`} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Podstawiamy{" "}
        <InlineMath math={`n = ${kLatex}`} /> do wzoru i skracamy potęgi:
      </p>
      <BlockMath math={step2_1} />
      <p>
        Zamiast liczby <InlineMath math={String(A)} /> wstawiamy{" "}
        <InlineMath math={`${c}\\cdot${base}^{${kMinus1Latex}}`} />:
      </p>
      <BlockMath math={step2_2} />
      <p>
        Potęga <InlineMath math={`${base}^{${kMinus1Latex}}`} /> w liczniku
        i mianowniku skraca się:
      </p>
      <BlockMath math={step2_3} />

      <p>
        <strong>Krok 3.</strong> Obliczamy wartość czynnika z{" "}
        <InlineMath math={'(-1)'} />:
      </p>
      <BlockMath
        math={`(-1)^{${kLatex}${eps === 0 ? "" : "+1"}} = (-1)^{${exponentLatex}}`}
      />
      <BlockMath math={step3_1} />
      <BlockMath math={step3_2} />

      <p>
        Zatem szukany wyraz ciągu jest równy{" "}
        <InlineMath math={finalValueLatex} />
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}
